import { Injectable } from '@angular/core';
import { Config } from '@environments/config';
import { BrowserExtensionConfig } from '@environments/config-interface';
import { ManualPromise } from '@local/common';
import { isEdge, isFirefox, isNativeWindow, isSafari, RpcBrowserExtensionChannel } from '@local/common-web';
import { delay } from '@local/ts-infra';
import { EmbedService } from '@shared/embed.service';
import { Logger } from '@unleash-tech/js-logger';
import { Rpc, RpcChannel, RpcObservableExtension, RpcWebPortChannel } from '@unleash-tech/js-rpc';
import { distinctUntilChanged, firstValueFrom, map, Observable, ReplaySubject } from 'rxjs';
import Semaphore from 'semaphore-async-await';
import { LogService } from '.';
import { TimerService } from './timer.service';

export interface BrowserExtension {
  version: number;
  rpc: Rpc;
  port: MessagePort;
}

export const CONTRACT_VERSION = 2;

@Injectable({
  providedIn: 'root',
})
export class BrowserExtensionService {
  private _current$: ReplaySubject<BrowserExtension> = new ReplaySubject(1);
  private _currentSet = false;
  private _sfFrame: any;
  private logger: Logger;
  private _connected = false;

  get connected() {
    return this._connected;
  }

  get connected$() {
    return this._current$.pipe(map((x) => !!x));
  }

  get version$(): Observable<number> {
    return this._current$.pipe(
      map((b) => b?.version),
      distinctUntilChanged()
    );
  }

  get port$(): Observable<MessagePort> {
    return this._current$.pipe(
      map((b) => b?.port),
      distinctUntilChanged()
    );
  }
  set current(value: BrowserExtension) {
    this._current$.next(value);
    this._connected = !!value;
  }

  constructor(
    private logService: LogService,
    private timer: TimerService,
    private embedService: EmbedService
  ) {
    this.logger = logService.scope('BrowserExtensionService');

    if (isNativeWindow()) {
      this.current = null;
      return;
    }

    this.attemptConnect();
    window.addEventListener('focus', async () => {
      this.attemptConnect();
    });

    let sub = null;
    this._current$.subscribe((c) => {
      if (!c) {
        if (!sub)
          sub = this.timer.register(
            () => {
              this.attemptConnect();
            },
            { visibleInterval: 5000 }
          );
        return;
      }
      if (sub) {
        this.timer.unregister(sub);
        sub = null;
      }
    });
  }

  async openSidebar() {
    if (!this.port$) {
      return;
    }
    const port = await firstValueFrom(this.port$);
    port?.postMessage({ type: 'unleash:ext:sidebar:open' });
  }

  // Firefox and some other browser do not support direct connect
  private async attemptConnectLegacy() {
    let viaParent = false;
    // set by content script once ready
    if (!document.documentElement.getAttribute('__unleash__init') && !this._sfFrame) {
      if (this.embedService && (await this.embedService.isExternalWebSite())) {
        this._currentSet = true;
        this.current = null;
        return;
      } else if (this.embedService) viaParent = true;
    }

    const mc = new MessageChannel();
    const mp = new ManualPromise();

    mc.port2.onmessage = (m) => {
      const type = m.data?.type;
      if (type === 'unleash:ext:disconnect') {
        if (!mp.status) mp.resolve(null);
        this._currentSet = true;
        this.current = null;
      }
      if (type === 'unleash:ext:connect:ack') {
        const c = new RpcWebPortChannel(mc.port2);
        if (!mp.status) mp.resolve(null);
        this._currentSet = true;

        this.current = { rpc: this.createRpc(c), version: m.data?.version, port: mc.port2 };
      }
    };
    setTimeout(() => {
      if (!mp.status) {
        this._currentSet = true;
        this.current = null;
        mp.resolve(null);
      }
    }, 2000);

    const win = viaParent ? window.parent : window;
    
    const targetOrigin = this.buildExtensionUrl();
    win?.postMessage({ type: 'unleash:ext:connect', port: mc.port1 }, targetOrigin, [mc.port1]);

    return mp;
  }

  private buildExtensionUrl(): string {
    const config = Config.browserExtension;

    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('chrome') || ua.includes('chromium')) {
      return `chrome-extension://${config.chromium.id}/`;
    }

    if (ua.includes('edg/')) {
      return `chrome-extension://${config.edge.id}/`;
    }

    if (ua.includes('firefox')) {
      return `moz-extension://${config.gecko.id}/`;
    }

    if (ua.includes('safari')) {
      return `safari-web-extension://${config.webkit.id}/`;
    }
  }

  private sem = new Semaphore(1);

  private async attemptConnect() {
    const rt = window.chrome?.runtime || (<any>window).browser?.runtime;
    await this.sem.acquire();

    if (this._currentSet && (await firstValueFrom(this._current$))) {
      this.sem.release();
      return;
    }

    if (!rt) {
      try {
        // firefox works with older exception types

        if (!window.chrome && !(<any>window).browser) await this.attemptConnectLegacy();
        else {
          this._currentSet = true;
          this.current = null;
        }
        return;
      } finally {
        this.sem.release();
      }
    }

    let done = false;
    let killed = false;

    // connect doesn't throw if the extension is not  there it will only call onDisconn+ect
    const config: BrowserExtensionConfig = Config.browserExtension;
    let ids = isSafari() ? [config.webkit.id] : isFirefox() ? [config.gecko.id] : [config.chromium.id];

    if (isEdge()) ids.unshift(config.edge.id);

    // edge has a thing on google related websites they are changing the userAgent not to include edge
    // it would be better always to rely in an embed on the embed id rather ids
    if (this.embedService && !(await this.embedService.isExternalWebSite())) {
      let id = await (await firstValueFrom(this.embedService.options$)).id;
      const PREF = 'extension:';
      if (id.startsWith(PREF)) {
        id = id.substring(PREF.length);
        ids = [id];
      }
    }
    let failures = 0;

    for (let id of ids) {
      try {
        const port = rt.connect(id);

        port.onDisconnect.addListener(() => {
          if (killed) return;
          if (++failures == ids.length) {
            killed = true;
            done = true;
            this.sem.release();
            this._currentSet = true;
            this.current = null;
          }
        });

        port.onMessage.addListener((e) => {
          if (done) return;

          if (e?.type == 'unleash:ext:connect:ack') {
            done = true;
            const c = new RpcBrowserExtensionChannel(port);
            this.sem.release();
            this._currentSet = true;
            const mc = new MessageChannel();
            mc.port2.addEventListener('message', (e) => {
              port.postMessage(e.data);
            });
            mc.port1.start();
            mc.port2.start();
            this.current = { rpc: this.createRpc(c), version: e.version, port: mc.port1 };
          }
        });

        const MAX_TIMEOUT = 2000;
        delay(MAX_TIMEOUT).then(() => {
          if (killed || done) return;
          if (++failures == ids.length) {
            killed = true;
            done = true;
            this.sem.release();
            this._currentSet = true;
            this.current = null;
          }
        });

        await port.postMessage({ type: 'unleash:ext:connect' });
      } catch (e) {
        if (killed) continue;
        if (++failures == ids.length) {
          done = true;
          killed = true;
          this.sem.release();
          this._currentSet = true;
          this.current = null;
        }
      }
    }
  }

  private createRpc(c: RpcChannel): Rpc {
    const rpc = new Rpc(c, this.logger);

    rpc.addExtension(new RpcObservableExtension());
    return rpc;
  }
}
